import React from 'react';
import { Mic, MicOff, Phone, PhoneOff, PhoneIncoming } from 'lucide-react';

import { AvailabilityDot } from './HotSeatTransferCard';
import { COPY, pickupCopy } from './pickup';

// The pick-up card (Nic 2026-11-02): bottom right, where the orphan banner
// sits. Three looks: the offer, the switch in progress, and "on the line"
// for a call that is not theirs (mute, hang up, one Direct hot transfer to
// the owner). Their own lead or client never shows this last look: the
// Workspace opens instead. While the switch is still being prepared (before
// the INVITE goes out, `canCancel`) a Cancel puts the offer back.
const PickupCard = ({
  pickup,
  muted = false,
  busy = false,
  onPickUp,
  onDismiss,
  onCancel,
  onToggleMute,
  onHangUp,
  onTransfer,
}) => {
  if (!pickup?.call) return null;
  const copy = pickupCopy(pickup.match, { number: pickup.call.otherNumber });
  const { phase } = pickup;
  const to = pickup.match?.transferTo || null;
  const lineBusy = to?.availability?.state === 'on_call';

  if (phase === 'online') {
    return (
      <div
        data-testid="pickup-card"
        className="fixed bottom-4 right-4 z-[1090] flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 shadow-lg dark:border-emerald-500/30 dark:!bg-emerald-950"
      >
        <PhoneIncoming
          size={16}
          className="shrink-0 text-emerald-700 dark:text-emerald-300"
        />
        <div className="text-xs text-emerald-900 dark:text-emerald-100">
          <span className="font-semibold">{copy.online}</span>
          {pickup.note ? (
            <span className="block text-emerald-800/80 dark:text-emerald-200/80">
              {pickup.note}
            </span>
          ) : null}
        </div>
        {to ? (
          <button
            type="button"
            disabled={busy || lineBusy}
            onClick={() => onTransfer?.(to)}
            title={
              lineBusy
                ? COPY.busyLine(copy.ownerFirst)
                : `${to.name}: ${to.availability?.detail || ''}`
            }
            className="inline-flex items-center gap-1.5 rounded-lg bg-orange-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-500 disabled:opacity-50"
          >
            <AvailabilityDot availability={to.availability} />
            {copy.transfer}
          </button>
        ) : null}
        <button
          type="button"
          onClick={onToggleMute}
          title={muted ? COPY.unmute : COPY.mute}
          aria-label={muted ? COPY.unmute : COPY.mute}
          className="rounded-lg bg-white p-1.5 text-emerald-800 hover:bg-emerald-100 dark:!bg-white/10 dark:text-emerald-100"
        >
          {muted ? <MicOff size={14} /> : <Mic size={14} />}
        </button>
        <button
          type="button"
          onClick={onHangUp}
          className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-500"
        >
          <PhoneOff size={14} />
          {COPY.hangUp}
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid="pickup-card"
      className="fixed bottom-4 right-4 z-[1090] flex items-center gap-3 rounded-xl border border-sky-200 bg-sky-50 px-3.5 py-2.5 shadow-lg dark:border-sky-500/30 dark:!bg-sky-950"
    >
      <Phone size={16} className="shrink-0 text-sky-700 dark:text-sky-300" />
      <div className="text-xs text-sky-900 dark:text-sky-100">
        <span className="font-semibold">{copy.offer}</span>
        {phase === 'switching' || phase === 'routing' ? (
          <span className="block text-sky-800/80 dark:text-sky-200/80">
            {COPY.switching}
          </span>
        ) : null}
      </div>
      {phase === 'offer' ? (
        <>
          <button
            type="button"
            disabled={busy}
            onClick={onPickUp}
            className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-500 disabled:opacity-50"
          >
            {COPY.pickUp}
          </button>
          <button
            type="button"
            onClick={onDismiss}
            className="rounded-lg bg-white px-2 py-1.5 text-xs font-semibold text-sky-700 hover:bg-sky-100 dark:!bg-white/10 dark:text-sky-100"
          >
            {COPY.leaveIt}
          </button>
        </>
      ) : phase === 'switching' && pickup.canCancel && onCancel ? (
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg bg-white px-2 py-1.5 text-xs font-semibold text-sky-700 hover:bg-sky-100 dark:!bg-white/10 dark:text-sky-100"
        >
          {COPY.cancel}
        </button>
      ) : null}
    </div>
  );
};

export default PickupCard;
